import React, { useEffect, useState, useRef } from 'react';
import cytoscape, { Core } from 'cytoscape';

interface OntologyClass {
  id: string;
  label: string;
  parent: string | null;
  definition: string;
  cardinality: string;
  disjointness: string;
  examples: string;
  usageNotes: string;
  children: string[];
}

interface OntologyIndividual {
  id: string;
  label: string;
  type: string;  // PIDScheme, License, DataFormat, etc.
  alternativeNames?: string;
  description?: string;
  reference?: string;
}

interface OntologyAlignment {
  source: string;  // FAIR class/property
  target: string;  // External concept URI
  vocabulary: string;  // DCAT, PROV, Schema.org, etc.
  relationType: string;  // equivalentClass, subClassOf, closeMatch, etc.
  notes?: string;
}

interface EnhancedOntologyData {
  version: string;
  created: string;
  source: string;
  classes: OntologyClass[];
  objectProperties: Array<{ id: string; label: string; parent: string; definition: string; domain: string }>;
  dataProperties: Array<{ id: string; label: string }>;
  individuals: OntologyIndividual[];
  alignments: OntologyAlignment[];
  hierarchy: {
    [key: string]: string[];
  };
  meta: {
    totalClasses: number;
    totalObjectProperties: number;
    totalDataProperties: number;
    totalIndividuals: number;
    totalAlignments: number;
    maxDepth: number;
  };
}

export default function OntologyStructureViewerAdvanced() {
  const [data, setData] = useState<EnhancedOntologyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<OntologyClass | null>(null);
  const [_expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['ROOT']));

  const cyRef = useRef<Core | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch ontology data
  useEffect(() => {
    async function fetchData() {
      try {
        const response = await fetch('/api/ontology/v1.1/complete');
        if (!response.ok) {
          throw new Error(`Failed to load ontology: ${response.statusText}`);
        }
        const jsonData = await response.json();
        console.log('[OntologyAdvanced] Loaded v1.1.0 Complete:', {
          classes: jsonData.meta.totalClasses,
          individuals: jsonData.meta.totalIndividuals,
          alignments: jsonData.meta.totalAlignments
        });
        setData(jsonData);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Initialize Cytoscape graph
  useEffect(() => {
    if (!data || !containerRef.current) return;

    // Track if component is still mounted
    let isMounted = true;

    const elements: Array<{ data: Record<string, unknown> }> = [];

    // Add ALL class nodes
    data.classes.forEach(cls => {
      elements.push({
        data: {
          id: cls.id,
          label: cls.label,
          type: 'class',
          hasChildren: cls.children.length > 0
        }
      });
    });

    // Add ALL object property relationships (this is the complexity!)
    data.objectProperties.forEach(prop => {
      // In the JSON structure: parent = domain (source), definition = range (target)
      const domainStr = prop.parent;
      const rangeStr = prop.definition;

      if (domainStr && rangeStr && domainStr !== 'None' && rangeStr !== 'None') {
        // Handle multiple domains (e.g., "DataService | Dataset")
        const domains = domainStr.split('|').map((d: string) => d.trim());
        const ranges = rangeStr.split('|').map((r: string) => r.trim());

        // Create an edge for each domain-range combination
        domains.forEach((domain: string, dIdx: number) => {
          ranges.forEach((range: string, rIdx: number) => {
            // Only create edge if both source and target classes exist
            const sourceExists = data.classes.some(c => c.id === domain);
            const targetExists = data.classes.some(c => c.id === range);

            if (sourceExists && targetExists) {
              elements.push({
                data: {
                  id: `${prop.id}-${dIdx}-${rIdx}-edge`,
                  source: domain,
                  target: range,
                  label: prop.label,
                  relType: 'objectProperty',
                  inverse: prop.domain // The 'domain' field actually contains the inverse property name
                }
              });
            }
          });
        });
      }
    });

    // Add subclass relationships (is-a hierarchy)
    data.classes.forEach(cls => {
      if (cls.parent) {
        // Only create edge if parent class exists in the ontology
        const parentExists = data.classes.some(c => c.id === cls.parent);
        if (parentExists) {
          elements.push({
            data: {
              id: `subclass-${cls.parent}-${cls.id}`,
              source: cls.parent,
              target: cls.id,
              label: 'subClassOf',
              relType: 'hierarchy'
            }
          });
        }
      }
    });

    // Add INDIVIDUAL nodes (75 real-world instances)
    data.individuals.forEach(individual => {
      elements.push({
        data: {
          id: individual.id,
          label: individual.label,
          type: 'individual',
          individualType: individual.type,
          description: individual.description,
          reference: individual.reference
        }
      });

      // Add edge from individual to its class (type instantiation)
      const classExists = data.classes.some(c => c.id === individual.type);
      if (classExists) {
        elements.push({
          data: {
            id: `instance-${individual.id}`,
            source: individual.id,
            target: individual.type,
            label: 'instanceOf',
            relType: 'instantiation'
          }
        });
      }
    });

    // Add EXTERNAL ALIGNMENT edges (56 vocabulary mappings)
    data.alignments.forEach((alignment, idx) => {
      // Check if source node exists in elements (must be a class node we already created)
      // Properties don't have their own nodes, only classes do
      const sourceNodeExists = elements.some(e => e.data.id === alignment.source && !e.data.source);

      if (sourceNodeExists) {
        // Create external concept node if it doesn't exist
        const externalNodeId = `external-${alignment.vocabulary}-${alignment.target.replace(/[^a-zA-Z0-9]/g, '_')}`;

        // Add external concept node
        if (!elements.find(e => e.data.id === externalNodeId)) {
          elements.push({
            data: {
              id: externalNodeId,
              label: alignment.target,
              type: 'external',
              vocabulary: alignment.vocabulary
            }
          });
        }

        // Add alignment edge
        elements.push({
          data: {
            id: `alignment-${idx}`,
            source: alignment.source,
            target: externalNodeId,
            label: alignment.relationType,
            relType: 'alignment',
            vocabulary: alignment.vocabulary,
            notes: alignment.notes
          }
        });
      }
    });

    // Count edge types
    const hierarchyEdges = elements.filter(e => e.data.relType === 'hierarchy').length;
    const objectPropertyEdges = elements.filter(e => e.data.relType === 'objectProperty').length;
    const instantiationEdges = elements.filter(e => e.data.relType === 'instantiation').length;
    const alignmentEdges = elements.filter(e => e.data.relType === 'alignment').length;

    const totalNodes = elements.filter(e => !e.data.source).length;
    const individualNodes = elements.filter(e => e.data.type === 'individual').length;
    const externalNodes = elements.filter(e => e.data.type === 'external').length;

    console.log('[OntologyAdvanced] v1.1.0 Complete Graph Loaded');
    console.log(`  Nodes: ${totalNodes} total (${data.classes.length} classes, ${individualNodes} individuals, ${externalNodes} external)`);
    console.log(`  Edges: ${elements.filter(e => e.data.source).length} total (${hierarchyEdges} hierarchy, ${objectPropertyEdges} object props, ${instantiationEdges} instantiation, ${alignmentEdges} alignments)`);
    console.log('[OntologyAdvanced] Container:', containerRef.current, 'Width:', containerRef.current?.offsetWidth, 'Height:', containerRef.current?.offsetHeight);

    // Initialize Cytoscape
    const cy = cytoscape({
      container: containerRef.current,
      elements,
      wheelSensitivity: 0.2,
      minZoom: 0.1,
      maxZoom: 3,
      style: [
        {
          selector: 'node',
          style: {
            'label': 'data(label)',
            'text-valign': 'center',
            'text-halign': 'center',
            'background-color': '#56B4E9',  // Colorblind-friendly sky blue (Okabe-Ito palette)
            'color': '#fff',
            'font-size': '12px',
            'font-weight': 'bold',
            'width': 100,  // Fixed width for classes
            'height': 100,
            'padding': '20px',
            'text-wrap': 'wrap',
            'text-max-width': '150px',
            'border-width': 3,
            'border-color': '#0072B2',  // Darker blue
            'shape': 'roundrectangle'  // Softer corners
          }
        },
        {
          selector: 'node[type = "individual"]',
          style: {
            'background-color': '#009E73',  // Bluish green for individuals
            'border-color': '#00634A',
            'shape': 'ellipse',              // Circles for individuals
            'width': 50,                     // Fixed small size
            'height': 50,
            'font-size': '10px',
            'padding': '0px'
          }
        },
        {
          selector: 'node[type = "external"]',
          style: {
            'background-color': '#D55E00',  // Vermillion for external concepts
            'border-color': '#A04700',
            'shape': 'diamond',              // Diamonds for external
            'width': 70,                     // Medium size
            'height': 70,
            'font-size': '9px',
            'padding': '0px'
          }
        },
        {
          selector: 'node:selected',
          style: {
            'border-width': 5,
            'border-color': '#E69F00'  // Colorblind-friendly orange
          }
        },
        {
          selector: 'edge',
          style: {
            'width': 2,
            'line-color': '#95a5a6',
            'target-arrow-color': '#95a5a6',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'label': 'data(label)',
            'font-size': '11px',
            'color': '#2c3e50',
            'text-background-color': '#ffffff',
            'text-background-opacity': 0.8,
            'text-background-padding': '3px',
            'text-rotation': 'autorotate',
            'text-margin-y': -10,
            'opacity': 0.7  // Reduce visual clutter
          }
        },
        {
          selector: 'edge[relType = "hierarchy"]',
          style: {
            'width': 4,
            'line-color': '#0072B2',  // Strong blue
            'target-arrow-color': '#0072B2',
            'line-style': 'solid',
            'opacity': 0.9
          }
        },
        {
          selector: 'edge[relType = "objectProperty"]',
          style: {
            'width': 2,
            'line-color': '#D55E00',  // Vermillion for semantic relationships
            'target-arrow-color': '#D55E00',
            'line-style': 'dashed',
            'line-dash-pattern': [6, 3],
            'opacity': 0.6
          }
        },
        {
          selector: 'edge[relType = "instantiation"]',
          style: {
            'width': 1,
            'line-color': '#009E73',  // Green for individual-to-class
            'target-arrow-color': '#009E73',
            'line-style': 'solid',
            'opacity': 0.4,
            'font-size': '9px'
          }
        },
        {
          selector: 'edge[relType = "alignment"]',
          style: {
            'width': 3,
            'line-color': '#E69F00',  // Orange for alignments
            'target-arrow-color': '#E69F00',
            'line-style': 'dashed',
            'line-dash-pattern': [4, 2],
            'opacity': 0.7,
            'font-size': '9px'
          }
        },
        {
          selector: 'edge:selected',
          style: {
            'opacity': 1.0,
            'width': 5
          }
        }
      ],
      layout: {
        name: 'cose',
        animate: true,
        animationDuration: 1500,
        nodeRepulsion: 20000,        // MUCH higher for 155+ nodes
        idealEdgeLength: 280,         // Longer edges for breathing room
        edgeElasticity: 60,           // Less tight clustering
        gravity: 0.15,                // Weaker central pull (spread out)
        numIter: 1500,                // More iterations for convergence
        componentSpacing: 200,        // More space between disconnected clusters
        nodeOverlap: 50,              // Minimum 50px spacing between nodes
        nestingFactor: 1.5,
        padding: 60
      }
    });

    console.log('[OntologyAdvanced] Cytoscape initialized, nodes:', cy.nodes().length, 'edges:', cy.edges().length);

    // Log node positions after layout
    setTimeout(() => {
      console.log('[OntologyAdvanced] Sample node positions:');
      cy.nodes().slice(0, 3).forEach(node => {
        console.log(`  ${node.data('label')}: x=${node.position('x')}, y=${node.position('y')}`);
      });
    }, 1000);

    // Handle node clicks for expansion
    cy.on('tap', 'node', (evt) => {
      if (!isMounted || !cyRef.current || cyRef.current.destroyed()) return;

      const node = evt.target;
      const nodeId = node.id();
      const nodeData = data.classes.find(c => c.id === nodeId);

      if (nodeData) {
        setSelectedNode(nodeData);

        // Toggle expansion if node has children
        if (nodeData.children.length > 0) {
          const isExpanded = node.data('expanded');

          if (isExpanded) {
            // Collapse: Remove children nodes and edges
            nodeData.children.forEach(childId => {
              cy.getElementById(childId).remove();
              cy.getElementById(`${nodeId}-${childId}`).remove();
            });

            setExpandedNodes(prev => {
              const newSet = new Set(prev);
              newSet.delete(nodeId);
              return newSet;
            });

            node.data('expanded', false);
          } else {
            // Expand: Add children nodes and edges
            const newElements: Array<{ data: Record<string, unknown> }> = [];

            nodeData.children.forEach(childId => {
              const childClass = data.classes.find(c => c.id === childId);
              if (childClass) {
                newElements.push({
                  data: {
                    id: childClass.id,
                    label: childClass.label,
                    hasChildren: childClass.children.length > 0,
                    expanded: false
                  }
                });

                newElements.push({
                  data: {
                    id: `${nodeId}-${childId}`,
                    source: nodeId,
                    target: childId
                  }
                });
              }
            });

            cy.add(newElements);

            setExpandedNodes(prev => new Set(prev).add(nodeId));
            node.data('expanded', true);

            // Re-layout
            cy.layout({
              name: 'breadthfirst',
              directed: true,
              padding: 50,
              spacingFactor: 1.5,
              animate: true,
              animationDuration: 500
            }).run();
          }
        }
      }
    });

    cyRef.current = cy;

    return () => {
      isMounted = false;

      // Properly clean up Cytoscape instance
      try {
        if (cyRef.current && typeof cyRef.current.destroyed === 'function' && !cyRef.current.destroyed()) {
          cyRef.current.removeAllListeners();
          cyRef.current.destroy();
        }
      } catch (err) {
        // Silently handle cleanup errors
        console.warn('[OntologyAdvanced] Cleanup error:', err);
      }

      cyRef.current = null;
    };
  }, [data]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading Enhanced FAIR Ontology...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', color: '#e74c3c' }}>
        <h2>Error Loading Ontology</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', backgroundColor: '#ecf0f1' }}>
      {/* Graph Container */}
      <div style={{ flex: 1, position: 'relative' }}>
        <div
          ref={containerRef}
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#fff'
          }}
        />

        {/* Navigation Help */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '1rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          fontSize: '11px',
          border: '1px solid rgba(0,0,0,0.08)',
          maxWidth: '200px'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '13px', fontWeight: 'bold', color: '#2c3e50' }}>
            Navigation
          </h4>
          <p style={{ margin: '0.3rem 0', color: '#4a5568', lineHeight: '1.4' }}>
            <strong>Pan:</strong> Click + drag background
          </p>
          <p style={{ margin: '0.3rem 0', color: '#4a5568', lineHeight: '1.4' }}>
            <strong>Zoom:</strong> Mouse wheel
          </p>
          <p style={{ margin: '0.3rem 0', color: '#4a5568', lineHeight: '1.4' }}>
            <strong>Select:</strong> Click node
          </p>
        </div>

        {/* Legend */}
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          padding: '1.25rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: '14px',
          border: '1px solid rgba(0,0,0,0.08)',
          maxWidth: '280px'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '16px', fontWeight: 'bold', color: '#2c3e50' }}>
            FAIR Ontology v{data?.version} Complete
          </h3>
          <p style={{ margin: '0.25rem 0', fontSize: '11px', color: '#4a5568', lineHeight: '1.4' }}>
            {data?.meta.totalClasses} classes • {data?.meta.totalIndividuals} individuals • {data?.meta.totalAlignments} alignments
          </p>

          <div style={{ marginTop: '0.75rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '13px', fontWeight: 'bold', color: '#2c3e50' }}>Node Types</h4>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.35rem' }}>
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#56B4E9',
                border: '2px solid #0072B2',
                borderRadius: '4px',
                marginRight: '0.6rem',
                flexShrink: 0
              }} />
              <span style={{ fontSize: '11px', color: '#2c3e50' }}>Classes (24)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.35rem' }}>
              <div style={{
                width: '18px',
                height: '18px',
                backgroundColor: '#009E73',
                border: '2px solid #00634A',
                borderRadius: '50%',
                marginRight: '0.6rem',
                marginLeft: '3px',
                flexShrink: 0
              }} />
              <span style={{ fontSize: '11px', color: '#2c3e50' }}>Individuals (75)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.35rem' }}>
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#D55E00',
                border: '2px solid #A04700',
                transform: 'rotate(45deg)',
                marginRight: '0.6rem',
                marginLeft: '2px',
                flexShrink: 0
              }} />
              <span style={{ fontSize: '11px', color: '#2c3e50' }}>External Concepts (56)</span>
            </div>
          </div>

          <div style={{ marginTop: '0.85rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '13px', fontWeight: 'bold', color: '#2c3e50' }}>Edge Types</h4>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.3rem' }}>
              <div style={{
                width: '30px',
                height: '4px',
                backgroundColor: '#0072B2',
                marginRight: '0.6rem',
                flexShrink: 0
              }} />
              <span style={{ fontSize: '10px', color: '#2c3e50' }}>subClassOf</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.3rem' }}>
              <div style={{
                width: '30px',
                height: '2px',
                backgroundColor: '#D55E00',
                borderTop: '2px dashed #D55E00',
                marginRight: '0.6rem',
                flexShrink: 0
              }} />
              <span style={{ fontSize: '10px', color: '#2c3e50' }}>Object Properties</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.3rem' }}>
              <div style={{
                width: '30px',
                height: '1px',
                backgroundColor: '#009E73',
                marginRight: '0.6rem',
                flexShrink: 0
              }} />
              <span style={{ fontSize: '10px', color: '#2c3e50' }}>instanceOf</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: '30px',
                height: '3px',
                backgroundColor: '#E69F00',
                borderTop: '3px dashed #E69F00',
                marginRight: '0.6rem',
                flexShrink: 0
              }} />
              <span style={{ fontSize: '10px', color: '#2c3e50' }}>Alignments</span>
            </div>
          </div>

          <div style={{ marginTop: '0.85rem', paddingTop: '0.6rem', borderTop: '1px solid #e2e8f0', fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
            Colorblind-friendly palette (Okabe-Ito)
          </div>
          <div style={{ marginTop: '0.85rem', paddingTop: '0.6rem', borderTop: '1px solid #e2e8f0' }}>
            <p style={{ margin: '0.25rem 0', fontSize: '12px', color: '#2c3e50' }}>
              <strong>Classes:</strong> {data?.meta.totalClasses}
            </p>
            <p style={{ margin: '0.25rem 0', fontSize: '12px', color: '#2c3e50' }}>
              <strong>Max Depth:</strong> {data?.meta.maxDepth}
            </p>
          </div>
        </div>
      </div>

      {/* Details Panel */}
      {selectedNode && (
        <div style={{
          width: '400px',
          backgroundColor: '#fff',
          padding: '2rem',
          overflowY: 'auto',
          borderLeft: '1px solid #ddd'
        }}>
          <h2 style={{ marginTop: 0, color: '#2c3e50' }}>{selectedNode.label}</h2>

          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '0.5rem' }}>
              Definition
            </h3>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>
              {selectedNode.definition || 'No definition available'}
            </p>
          </div>

          {selectedNode.cardinality && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                Cardinality
              </h3>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {selectedNode.cardinality}
              </p>
            </div>
          )}

          {selectedNode.examples && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                Examples
              </h3>
              <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>
                {selectedNode.examples}
              </p>
            </div>
          )}

          {selectedNode.usageNotes && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                Usage Notes
              </h3>
              <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>
                {selectedNode.usageNotes}
              </p>
            </div>
          )}

          {selectedNode.disjointness && selectedNode.disjointness !== 'None' && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                Disjoint With
              </h3>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {selectedNode.disjointness}
              </p>
            </div>
          )}

          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '0.5rem' }}>
              Hierarchy
            </h3>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '14px' }}>
              <strong>Parent:</strong> {selectedNode.parent || 'Thing (root)'}
            </p>
            {selectedNode.children.length > 0 && (
              <p style={{ margin: 0, fontSize: '14px' }}>
                <strong>Children:</strong> {selectedNode.children.join(', ')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
